/**
 * Same-session elapsed measurement domain (#850, parent #847).
 *
 * Durations that exist only inside one page session — a sampling interval, a
 * history window, a retention horizon, contracts-per-second — must never be
 * derived by subtracting two `Date.now()` stamps. The browser wall clock is
 * adjustable: an NTP correction or a manual change while an order is resting
 * rewrites the denominator of every rate and the age of every history point.
 *
 * `elapsedMs` here is `performance.now()` and nothing else. Deliberately NOT
 * `relayClock.elapsedSince`'s `max(wall, mono)`: that is a conservative AGE
 * BOUND, correct for expiring an alert early, and wrong as a measured rate —
 * a forward wall step would still inflate the interval and deflate the rate.
 *
 * Epoch timestamps remain epochs. This domain is for elapsed measurement only;
 * anything that must survive a reload, identify a physical reading, or cross a
 * process boundary still needs real wall time. See docs/clock-domain-safety.md.
 *
 * ## `generation` — when measured history must be thrown away
 *
 * The monotonic clock is same-context, so a reading is only comparable with
 * another reading from the same uninterrupted context. Three events break that,
 * and each bumps `generation`; a consumer that sees a new generation must clear
 * its MEASURED history and start from a fresh baseline. It must not clear
 * authoritative state — mutes, orders, fills — which this clock knows nothing
 * about.
 *
 * 1. The monotonic clock runs backward or restarts. Observed at any read.
 * 2. The page was hidden and, across that hidden interval, wall time advanced
 *    at least `SUSPENSION_TOLERANCE_MS` more than monotonic time. Some
 *    platforms pause `performance.now()` while the host sleeps, which would
 *    otherwise compress an unobserved hour into a couple of seconds and read a
 *    routine queue change as a violent one.
 * 3. Both an independent SOURCE clock and the local wall clock outran us by
 *    at least the same tolerance — see `observeSourceTime`. This can see a
 *    suspension the page never noticed, because the page was never hidden.
 *
 * (2) uses the same tolerance as the proven suspension check in
 * `useUserKalshiPOS.ts`, but is deliberately ONE-SIDED where that one is
 * symmetric, and the difference is a contract difference, not an oversight.
 * Invalidating there is cheap: the queue clock re-anchors on the next request,
 * so any wall/monotonic disagreement is worth acting on. Resetting here throws
 * away up to 65 minutes of real observation, so it is spent only on evidence
 * that monotonic time stopped covering the interval. A backward wall
 * correction is evidence about the WALL clock, which this domain never reads.
 *
 * An ORDINARY ACTIVE SESSION NEVER BUMPS `generation`. In particular a wall
 * step of any size or direction, with the page visible and the monotonic clock
 * healthy, changes nothing: monotonic time is the measurement, so there is
 * nothing to correct. That is the point of the domain, and #850's acceptance
 * requires it — a clock adjustment must neither manufacture nor suppress the
 * result an uninterrupted session would have produced.
 *
 * A host that sleeps while the page stays VISIBLE fires no visibility event,
 * and from LOCAL clocks alone it is indistinguishable from a forward wall step.
 * That is what `observeSourceTime` is for: a producer's clock is independent of
 * this machine's, so source time outrunning our elapsed time is evidence of a
 * gap we did not observe, whatever the local wall clock did.
 */

/** Divergence — wall-vs-monotonic across a hidden interval, or source-vs-
 * monotonic between two readings — taken as evidence that monotonic time
 * stopped covering the interval rather than merely being imprecise. */
const SUSPENSION_TOLERANCE_MS = 1_000;

export interface SessionElapsedReading {
  /** Monotonic milliseconds since this browsing context started. Comparable
   *  only with other readings carrying the same `generation`. */
  elapsedMs: number;
  /** Increments when earlier readings stopped being comparable to later ones. */
  generation: number;
}

interface ClockStamp {
  wallMs: number;
  monoMs: number;
}

function stamp(): ClockStamp {
  return { wallMs: Date.now(), monoMs: performance.now() };
}

export class SessionElapsedClock {
  private generation = 0;
  private lastMonoMs: number | null = null;
  private hiddenAt: ClockStamp | null = null;
  private lastSource: { sourceMs: number; monoMs: number; wallMs: number } | null = null;

  read(): SessionElapsedReading {
    const monoMs = performance.now();
    // A restarted or rewound monotonic clock makes every stored measurement
    // stamp meaningless, including ones that would now look like the future.
    if (this.lastMonoMs !== null && monoMs < this.lastMonoMs) this.generation += 1;
    this.lastMonoMs = monoMs;
    return { elapsedMs: monoMs, generation: this.generation };
  }

  /**
   * Report an observation of an INDEPENDENT clock — a producer/relay timestamp
   * that advances with real time on another machine — so this clock can notice
   * an interval that real time covered and monotonic time did not.
   *
   * The comparison is between successive readings of the SAME source, against
   * our own elapsed time over the same span. Absolute offset between the two
   * machines cancels; only the rates are compared, so this never subtracts one
   * clock domain from another to produce an age.
   *
   * One-sided, with local wall-clock corroboration. Source time outrunning us
   * alone can also mean delayed delivery caught up; it is not proof of sleep.
   * Source time LAGGING us is ordinary:
   * an unchanged physical reading is not republished (the push layer keeps
   * volatile age fields out of its dedupe hash), so a quiet source falls behind
   * a live page all the time and means nothing.
   *
   * Callers pass a raw source epoch. A source clock step alone does not reset
   * history. Simultaneous local wall adjustment and source-delivery catch-up
   * remain indistinguishable from sleep and conservatively reset measurements.
   *
   * Not to be confused with `src/lib/sourceAge.ts`, which shares the words
   * "source" and "generation" and answers a different question. That module
   * classifies how OLD a Hydra market/book frame is, per socket. This one
   * never produces an age: it reads only the RATE of a source clock, to learn
   * something about the LOCAL one.
   */
  observeSourceTime(sourceMs: number): void {
    if (!Number.isFinite(sourceMs)) return;
    const monoMs = performance.now();
    const wallMs = Date.now();
    const last = this.lastSource;
    // A replayed or older reading is not a new observation of the source.
    if (last && sourceMs <= last.sourceMs) return;
    // Source delivery can catch up after network/event-loop delay even when
    // the local clock never paused. Require an independent LOCAL elapsed gap
    // too; neither transport jitter nor a local wall adjustment alone is sleep.
    if (last
      && sourceMs - last.sourceMs - (monoMs - last.monoMs) >= SUSPENSION_TOLERANCE_MS
      && wallMs - last.wallMs - (monoMs - last.monoMs) >= SUSPENSION_TOLERANCE_MS) {
      this.generation += 1;
    }
    this.lastSource = { sourceMs, monoMs, wallMs };
  }

  /** The page stopped being observed. Remembers both clocks so the resume can
   *  tell a paused monotonic clock from an ordinary hidden interval. */
  markHidden(): void {
    this.hiddenAt = stamp();
  }

  /** The page is observed again. Bumps `generation` only when the hidden
   *  interval carries evidence that monotonic time did not cover all of it. */
  markVisible(): void {
    const hiddenAt = this.hiddenAt;
    this.hiddenAt = null;
    if (!hiddenAt) return;
    const wallDeltaMs = Date.now() - hiddenAt.wallMs;
    const monoDeltaMs = performance.now() - hiddenAt.monoMs;
    if (wallDeltaMs - monoDeltaMs >= SUSPENSION_TOLERANCE_MS) this.generation += 1;
  }
}

/** The one clock every in-session elapsed measurement shares, so a single
 *  discontinuity is observed identically by every consumer. */
export const sessionElapsedClock = new SessionElapsedClock();

export function readSessionElapsed(): SessionElapsedReading {
  return sessionElapsedClock.read();
}

/** Feed the shared clock an independent source timestamp. See
 *  `SessionElapsedClock.observeSourceTime`. */
export function observeSourceTime(sourceMs: number): void {
  sessionElapsedClock.observeSourceTime(sourceMs);
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') sessionElapsedClock.markVisible();
    else sessionElapsedClock.markHidden();
  });
}
